import { Component, computed, DestroyRef, ElementRef, inject, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ActionButtonsComponent } from './action-buttons/action-buttons.component';
import { RelatedPreviewsComponent } from './related-previews/related-previews.component';
import { DvmService } from '@services/dvm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { MinimapComponent } from './minimap/minimap.component';
import { DisclaimerComponent } from './disclaimer/disclaimer.component';
import { SearchComponent } from '../topbar/navigation/search/search.component';
import { MenuButtonsComponent } from '../topbar/menu/menu-buttons/menu-buttons.component';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import anime from 'animejs';
import { BenefitsButtonComponent } from "../topbar/benefits/benefits-button/benefits-button.component";
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';

@Component({
    selector: 'app-footer',
    imports: [
        SearchComponent,
        MenuButtonsComponent,
        MinimapComponent,
        RelatedPreviewsComponent,
        ActionButtonsComponent,
        DisclaimerComponent,
        BenefitsButtonComponent
    ],
    templateUrl: './footer.component.html',
    styleUrl: './footer.component.css'
})
export class FooterComponent implements OnInit {

  // VIEW CHILDS
  @ViewChild('footerContainer', { static: true })
  private readonly footerContainer!:  ElementRef<HTMLElement>;

  // SERVICES
  private readonly dvmService:        DvmService               = inject(DvmService);
  private readonly destroyRef:        DestroyRef               = inject(DestroyRef);
  private readonly routeParams:       ViewerRouteParamsService = inject(ViewerRouteParamsService);

  // GLOBAL STATE
  protected readonly view:            Signal<AppViews|null> = this.dvmService.view;
  protected readonly AppViews:        typeof AppViews       = AppViews;
  protected readonly hyperlinkMode:   boolean               = this.routeParams.hasViewId;
  // Top level = Topview/Main (or view not yet determined); false on Seatmap/Pano.
  // Search shows on both top-level views, so its wrapper must accept clicks on both.
  protected readonly isTopLevelView:  Signal<boolean>       = computed((): boolean => {
    const view: AppViews | null = this.view();
    return view !== AppViews.Seatmap && view !== AppViews.Pano;
  });

  // COMPONENT STATE
  protected readonly hideElement:     WritableSignal<boolean> = signal(false);

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
  }

  // METHODS

  /**
   * Iniciamos el footer esperando a que cargue el viewer 3d para suscribirnos a sus triggers.
   */
  private initComponent(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSuscriptions());

  }

  /**
   * Escuchamos los triggers de full screen para mostrar u ocultar el footer.
   */
  private initSuscriptions(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.getObservable('fullscreen_enabled').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.toggleVisibility(false));
    viewer3d.getObservable('fullscreen_disabled').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(():void => this.toggleVisibility(true));

  }

  /**
   * Muestra u oculta el footer dependiendo del estado de la variable show.
   * Si hideElement es true, significa que el footer está oculto y no se puede mostrar, por ello si show es true, revertimos este estado
   * antes de la animación. 
   * @param {boolean} show 
   */
  private toggleVisibility(show: boolean): void {
    if (this.hyperlinkMode) return;

    if(show && this.hideElement()) this.hideElement.set(false);

    anime({
      targets:  this.footerContainer.nativeElement,
      opacity:  show ? 1 : 0,
      easing:   'easeInOutQuad',
      duration: 300,
      complete: (): void => { if(!show && !this.hideElement()) this.hideElement.set(true); },
    })

  }


}
